import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utils/axios';

interface Contact {
  id: string;
  phoneNumber: string;
  name?: string;
  avatar?: string;
  isBlocked: boolean;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  chats?: any[];
}

const ContactsView: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const limit = 50; // Show 50 contacts per page

  useEffect(() => {
    loadContacts();
  }, [currentPage]);

  // Debounce search - reload contacts when search term changes
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      loadContacts();
    }, 500); // Wait 500ms after user stops typing
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      // axios already has /api as baseURL and auto-adds auth header
      const params: any = {
        page: currentPage,
        limit: limit
      };
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await axios.get('/contacts', { params });
      if (response.data.success) {
        setContacts(response.data.data);
        if (response.data.pagination) {
          setTotalContacts(response.data.pagination.total);
          setTotalPages(response.data.pagination.pages);
        }
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleAddContact = async (contactData: any) => {
    try {
      // axios already has /api as baseURL
      const response = await axios.post('/contacts', contactData);
      if (response.data.success) {
        setContacts([response.data.data, ...contacts]);
        setShowAddModal(false);
        alert('Contact added successfully!');
      }
    } catch (error: any) {
      console.error('Error adding contact:', error);
      alert(error.response?.data?.error || 'Failed to add contact');
    }
  };

  const handleUpdateContact = async (id: string, contactData: any) => {
    try {
      // axios already has /api as baseURL
      const response = await axios.put(`/contacts/${id}`, contactData);
      if (response.data.success) {
        setContacts(contacts.map(c => c.id === id ? response.data.data : c));
        setShowEditModal(false);
        setSelectedContact(null);
        alert('Contact updated successfully!');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      alert('Failed to update contact');
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      // axios already has /api as baseURL
      await axios.delete(`/contacts/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      setShowEditModal(false);
      setSelectedContact(null);
      alert('Contact deleted successfully!');
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact');
    }
  };

  const handleToggleBlock = async (contact: Contact) => {
    try {
      // axios already has /api as baseURL
      const response = await axios.put(`/contacts/${contact.id}`, { isBlocked: !contact.isBlocked });
      if (response.data.success) {
        setContacts(contacts.map(c => c.id === contact.id ? response.data.data : c));
      }
    } catch (error) {
      console.error('Error toggling block:', error);
      alert('Failed to update contact');
    }
  };

  const handleCSVImport = async (file: File) => {
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/contacts/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const results = response.data.data;
        let message = `Import completed!\n\n`;
        message += `✅ Success: ${results.success}\n`;
        message += `⏭️ Skipped: ${results.skipped}\n`;
        message += `❌ Failed: ${results.failed}`;

        if (results.errors.length > 0 && results.errors.length <= 10) {
          message += `\n\nErrors:\n${results.errors.slice(0, 10).join('\n')}`;
        } else if (results.errors.length > 10) {
          message += `\n\nFirst 10 errors:\n${results.errors.slice(0, 10).join('\n')}`;
          message += `\n... and ${results.errors.length - 10} more errors`;
        }

        alert(message);
        setShowImportModal(false);
        loadContacts(); // Reload contacts list
      }
    } catch (error: any) {
      console.error('Error importing CSV:', error);
      alert(error.response?.data?.error || 'Failed to import CSV file');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="contacts-view">
      <div className="contacts-header">
        <div>
          <h2 className="contacts-title">Contacts</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            {loading ? 'Loading...' : `${totalContacts.toLocaleString()} total contacts`}
            {searchTerm && ` (${totalContacts} found)`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="cancel-button"
            onClick={() => setShowImportModal(true)}
            style={{ padding: '10px 20px' }}
          >
            📥 Import CSV
          </button>
        <button 
          className="add-contact-button"
          onClick={() => setShowAddModal(true)}
        >
          + Add Contact
        </button>
        </div>
      </div>

      <div className="search-container" style={{ marginBottom: '24px' }}>
        <input
          type="text"
          placeholder="Search contacts by name or phone..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
          Loading contacts...
        </div>
      ) : contacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.3 }}>👥</div>
          <h4>No contacts found</h4>
          <p style={{ marginTop: '8px' }}>
            {searchTerm ? 'Try a different search term' : 'Add your first contact to get started'}
          </p>
        </div>
      ) : (
        <>
          <div className="contacts-grid">
            {contacts.map((contact) => (
            <div 
              key={contact.id} 
              className="contact-card"
              onClick={() => {
                setSelectedContact(contact);
                setShowEditModal(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="contact-header">
                <div className="avatar">
                  {contact.avatar ? (
                    <img src={contact.avatar} alt={contact.name || contact.phoneNumber} />
                  ) : (
                    getInitials(contact.name || contact.phoneNumber)
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="contact-name">
                    {contact.name || contact.phoneNumber}
                  </div>
                  <div className="contact-phone">{contact.phoneNumber}</div>
                </div>
                {contact.isBlocked && (
                  <span style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>
                    Blocked
                  </span>
                )}
              </div>
              
              {contact.notes && (
                <div style={{ 
                  fontSize: '13px', 
                  color: '#6b7280', 
                  marginTop: '8px',
                  marginBottom: '8px',
                  lineHeight: '1.4'
                }}>
                  {contact.notes}
                </div>
              )}
              
              {contact.tags && contact.tags.length > 0 && (
                <div className="contact-tags">
                  {contact.tags.map((tag, index) => (
                    <span key={index} className="contact-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {contact.chats && contact.chats.length > 0 && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#9ca3af', 
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  {contact.chats[0]._count?.messages || 0} messages
                </div>
              )}
            </div>
          ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '12px', 
              marginTop: '32px',
              padding: '20px'
            }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="cancel-button"
                style={{ 
                  padding: '8px 16px',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>
              
              <span style={{ 
                fontSize: '14px', 
                color: '#6b7280',
                minWidth: '120px',
                textAlign: 'center'
              }}>
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="cancel-button"
                style={{ 
                  padding: '8px 16px',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <ContactFormModal
          title="Add New Contact"
          onClose={() => setShowAddModal(false)}
          onSave={handleAddContact}
        />
      )}

      {/* Edit Contact Modal */}
      {showEditModal && selectedContact && (
        <ContactFormModal
          title="Edit Contact"
          contact={selectedContact}
          onClose={() => {
            setShowEditModal(false);
            setSelectedContact(null);
          }}
          onSave={(data) => handleUpdateContact(selectedContact.id, data)}
          onDelete={() => handleDeleteContact(selectedContact.id)}
          onToggleBlock={() => handleToggleBlock(selectedContact)}
        />
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <CSVImportModal
          onClose={() => setShowImportModal(false)}
          onImport={handleCSVImport}
          importing={importing}
        />
      )}
    </div>
  );
};

// Contact Form Modal Component
interface ContactFormModalProps {
  title: string;
  contact?: Contact;
  onClose: () => void;
  onSave: (data: any) => void;
  onDelete?: () => void;
  onToggleBlock?: () => void;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  title,
  contact,
  onClose,
  onSave,
  onDelete,
  onToggleBlock
}) => {
  const [formData, setFormData] = useState({
    phoneNumber: contact?.phoneNumber || '',
    firstName: (contact as any)?.firstName || '',
    lastName: (contact as any)?.lastName || '',
    notes: contact?.notes || '',
    tags: contact?.tags?.join(', ') || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      name: `${formData.firstName} ${formData.lastName}`.trim() || formData.firstName || formData.lastName,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    };
    onSave(dataToSave);
  };

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="assignment-header">
          <h4>{title}</h4>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="assignment-content">
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                className="form-input"
                placeholder="+1234567890"
                required
                disabled={!!contact}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="form-input"
                  placeholder="John"
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="form-input"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input"
                placeholder="Add notes about this contact..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Tags (comma separated)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="form-input"
                placeholder="VIP, Customer, Lead"
              />
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Separate tags with commas
              </div>
            </div>
          </div>

          <div className="assignment-actions">
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="danger-button"
                style={{ marginRight: 'auto' }}
              >
                Delete
              </button>
            )}
            {onToggleBlock && contact && (
              <button
                type="button"
                onClick={onToggleBlock}
                className="cancel-button"
              >
                {contact.isBlocked ? 'Unblock' : 'Block'}
              </button>
            )}
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="assign-button">
              {contact ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// CSV Import Modal Component
interface CSVImportModalProps {
  onClose: () => void;
  onImport: (file: File) => void;
  importing: boolean;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({ onClose, onImport, importing }) => {
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file) {
      onImport(file);
    }
  };

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="assignment-header">
          <h4>Import Contacts from CSV</h4>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="assignment-content">
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                Upload a CSV file with the following columns:
              </p>
              <div style={{ 
                backgroundColor: '#f9fafb', 
                padding: '12px', 
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: 'monospace',
                marginBottom: '16px'
              }}>
                <div><strong>Required:</strong></div>
                <div>• phone / phoneNumber</div>
                <div style={{ marginTop: '8px' }}><strong>Optional:</strong></div>
                <div>• name (or firstName + lastName)</div>
                <div>• firstName</div>
                <div>• lastName</div>
                <div>• tags (comma-separated)</div>
                <div>• notes</div>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                CSV File *
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="form-input"
                required
                disabled={importing}
              />
              {file && (
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>

            <div style={{ 
              backgroundColor: '#fef3c7', 
              padding: '12px', 
              borderRadius: '8px',
              fontSize: '12px',
              color: '#92400e',
              marginTop: '16px'
            }}>
              <strong>Note:</strong> Contacts with duplicate phone numbers will be skipped. Existing contacts will not be updated.
            </div>
          </div>

          <div className="assignment-actions">
            <button 
              type="button" 
              onClick={onClose} 
              className="cancel-button"
              disabled={importing}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="assign-button"
              disabled={!file || importing}
            >
              {importing ? 'Importing...' : 'Import CSV'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactsView;